'use client';

import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@/app/components/ui/use-toast';
import { useGlobalModal } from '@/app/hooks';
import type { LiveAvatar, LiveV1AvatarWithVoice } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { NEW_CHARACTER_ICON } from '../_constants/media';
import { deleteUserAvatar, listPresetAvatars, listUserAvatars } from '../apis';
import type { LiveReturnOrigin } from '../live-return-origin';
import { getAvatarVoiceLibrary, isUserBannerAvatar, reportStreamElementClick } from '../utils';
import { BannerAvatarCard } from './BannerAvatarCard';
import { BannerAvatarCardSkeleton } from './BannerAvatarCardSkeleton';
import { ShareAvatarDialog } from './ShareAvatarDialog';
import { useLiveClaimFlow } from './use-live-claim-flow';

const CreateMyOwnScreen = dynamic(
  () => import('./create-my-own/CreateMyOwnScreen').then((mod) => mod.CreateMyOwnScreen),
  { ssr: false },
);

/** UGC 数量信息，仅 user 分支返回；guest / 上限未知时为 null。 */
interface BannerAvatarCounts {
  active: number;
  max: number;
}

interface BannerRolesData {
  ugcRoles: LiveAvatar[];
  presetRoles: LiveAvatar[];
  counts: BannerAvatarCounts | null;
}

// 模块级缓存：跨路由切换（如从 /vidu-stream/live 返回）保留数据，命中即跳过初次请求。
// 登录/登出等主动场景会走 refreshBannerRoles 覆盖，无需 TTL。
let bannerRolesCache: BannerRolesData | null = null;

// UGC 块一行 6 槽（含新建卡）。
const UGC_ROW_SLOTS = 6;
// 有 UGC 时 PGC 独立成块，展示 12 个（2 行）。
const PRESET_DISPLAY_COUNT = 12;
// 无 UGC 时合并单块：新建卡 + 11 预设 = 12（2 行）。
const PRESET_DISPLAY_COUNT_MERGED = 11;
// 首个骨架屏 + 11 个 = 12，占满 2 行（6 列）。
const BANNER_SKELETON_KEYS = Array.from({ length: 11 }, (_, i) => `banner-skeleton-${i + 1}`);

interface BannerRoleSectionProps {
  className?: string;
  /** 所在页，决定直播结束后「返回角色列表」回哪。/vidu-stream 与 /s 共用本组件。 */
  origin: LiveReturnOrigin;
}

export function BannerRoleSection({ className = '', origin }: BannerRoleSectionProps) {
  const t = useTranslations();
  const { toast } = useToast();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  const [ugcRoles, setUgcRoles] = useState<LiveAvatar[]>(() => bannerRolesCache?.ugcRoles ?? []);
  const [presetRoles, setPresetRoles] = useState<LiveAvatar[]>(() => bannerRolesCache?.presetRoles ?? []);
  const [counts, setCounts] = useState<BannerAvatarCounts | null>(() => bannerRolesCache?.counts ?? null);
  const [isLoading, setIsLoading] = useState(bannerRolesCache === null);
  const hasFetchedRef = useRef(false);

  const [isCreateMyOwnOpen, setIsCreateMyOwnOpen] = useState(false);
  const [createMyOwnInitialData, setCreateMyOwnInitialData] = useState<LiveV1AvatarWithVoice | undefined>();
  const [shareTarget, setShareTarget] = useState<LiveAvatar | null>(null);

  const refreshBannerRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      // UGC / PGC 分开拉取，各自可独立翻页。UGC 失败（如游客无 token）静默降级为空，
      // 只要 PGC 拿到就正常渲染；PGC 失败才丢弃整批（保留上次数据）。
      const [ugcRes, presetRes] = await Promise.all([listUserAvatars(), listPresetAvatars()]);
      if (presetRes.error) {
        return;
      }

      const ugcData = ugcRes.error ? undefined : ugcRes.data;
      const active = ugcData?.active_avatar_count;
      const max = ugcData?.max_avatar_count;
      const next: BannerRolesData = {
        ugcRoles: ugcData?.roles ?? [],
        presetRoles: presetRes.data?.roles ?? [],
        // active/max 都拿到才算有效上限信息；否则视为未知（放行创建）。
        counts: typeof active === 'number' && typeof max === 'number' ? { active, max } : null,
      };

      bannerRolesCache = next;
      setUgcRoles(next.ugcRoles);
      setPresetRoles(next.presetRoles);
      setCounts(next.counts);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLoggedIn = useCallback(() => {
    void refreshBannerRoles();
  }, [refreshBannerRoles]);

  const { claimingAvatarId, startLiveCall, runGuarded, modals } = useLiveClaimFlow({
    origin,
    onLoggedIn: handleLoggedIn,
  });

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    // 已有模块级缓存（如从直播房间返回）就直接用，不重发请求。
    if (bannerRolesCache !== null) return;
    void refreshBannerRoles();
  }, [refreshBannerRoles]);

  const handleCreateAvatarClick = () => {
    reportStreamElementClick({ element_name: 'ugc_character_create_click' });
    runGuarded('vidu-stream - create-avatar', () => {
      setCreateMyOwnInitialData(undefined);
      setIsCreateMyOwnOpen(true);
    });
  };

  /** 点编辑：直接用 banner list 已有的 UGC 数据回填编辑界面。
   * 不能再调 keeperGetOrCreateUserAvatar：那是"获取或创建 draft"，会重置 draft 返回空数据。 */
  const handleEditOwnAvatar = (role: LiveAvatar) => {
    reportStreamElementClick({
      element_name: 'ugc_edit_click',
    });
    runGuarded('vidu-stream - edit-own-avatar', () => {
      setCreateMyOwnInitialData({
        avatar: role,
        voice_library: getAvatarVoiceLibrary(role),
      });
      setIsCreateMyOwnOpen(true);
    });
  };

  const handleCloseCreateMyOwn = () => {
    setIsCreateMyOwnOpen(false);
    setCreateMyOwnInitialData(undefined);
  };

  // 埋点在 startLiveCall 内统一上报（含 click_position），此处不再重复。
  const handleBannerRoleClick = (avatarId: string, role: LiveAvatar) => {
    startLiveCall(avatarId, role, 'vidu-stream - banner-role');
  };

  /**
   * 分享只是复制链接，不涉及声纹/面部采集，因此不走个人信息授权。
   * （曾被接入授权门禁，见 commit 93cc16dc11；这里回退掉。）
   */
  const handleShareRole = (role: LiveAvatar) => {
    reportEvent('stream_element_click', {
      element_name: 'mainpage_share_element_click',
      stream_character_name: role.name ?? '',
      stream_character_id: role.id ?? '',
      stream_character_type: isUserBannerAvatar(role) ? 'ugc' : 'pgc',
    });
    setShareTarget(role);
  };

  /** 点删除：仅弹二次确认。自建角色卡只在已登录且已建角色时渲染，无需再走登录/consent。 */
  const handleDeleteOwnAvatar = (role: LiveAvatar) => {
    reportStreamElementClick({ element_name: 'ugc_delete_click' });
    const trimmedId = role.id?.trim();
    if (!trimmedId) return;

    openConfirmDialog({
      title: t('vidu-stream.banner.deleteConfirmTitle'),
      description: t('vidu-stream.banner.deleteConfirmMessage'),
      primaryButtonText: t('vidu-stream.banner.deleteConfirmConfirm'),
      secondaryButtonText: t('vidu-stream.banner.deleteConfirmCancel'),
      onPrimaryAction: async () => {
        try {
          const { error } = await deleteUserAvatar(trimmedId);
          if (error) {
            toast({ type: 'error', message: t('live.internalErrorNotice') });
            return;
          }
          await refreshBannerRoles();
        } catch {
          toast({ type: 'error', message: t('live.internalErrorNotice') });
        }
      },
    });
  };

  const cardLabels = {
    chat: t('vidu-stream.banner.chat'),
    edit: t('vidu-stream.banner.edit'),
    delete: t('vidu-stream.banner.delete'),
    share: t('vidu-stream.avatars.menu.share'),
  };

  // active ≥ max 时隐藏新建卡；counts 为 null（guest / 上限未知）时放行。
  const canCreate = counts == null || counts.active < counts.max;
  const hasUgc = ugcRoles.length > 0;

  const renderCreateCard = () => (
    <button
      id="create-avatar"
      type="button"
      onClick={handleCreateAvatarClick}
      className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-3 rounded-16 border border-system-white16 bg-[rgba(44,44,45,0.70)] text-system-white backdrop-blur-[40px] transition-colors hover:border-system-white48 hover:bg-[rgba(60,60,61,0.7)]"
    >
      <img src={NEW_CHARACTER_ICON} alt="" className="h-[77px] w-auto" />
      <span className="px-3 text-center font-medium text-sm">{t('vidu-stream.banner.newCharacter')}</span>
    </button>
  );

  const renderUgcCard = (role: LiveAvatar) => {
    const avatarId = role.id?.trim() ?? '';
    if (!avatarId) {
      return null;
    }
    return (
      <BannerAvatarCard
        key={avatarId}
        avatarId={avatarId}
        role={role}
        isOwn
        inlineFooter
        isClaiming={claimingAvatarId === avatarId}
        className="w-full sm:w-full"
        chatLabel={cardLabels.chat}
        editLabel={cardLabels.edit}
        shareLabel={cardLabels.share}
        deleteLabel={cardLabels.delete}
        onChat={handleBannerRoleClick}
        onEdit={handleEditOwnAvatar}
        onShare={handleShareRole}
        onDelete={handleDeleteOwnAvatar}
      />
    );
  };

  const renderPresetCard = (role: LiveAvatar) => {
    const avatarId = role.id?.trim() ?? '';
    if (!avatarId) {
      return null;
    }
    return (
      <BannerAvatarCard
        key={avatarId}
        avatarId={avatarId}
        role={role}
        isOwn={false}
        isClaiming={claimingAvatarId === avatarId}
        className="w-full sm:w-full"
        chatLabel={cardLabels.chat}
        editLabel={cardLabels.edit}
        onChat={handleBannerRoleClick}
        inlineFooter
        onShare={handleShareRole}
      />
    );
  };

  // 合并单块（无 UGC）：新建卡占 1 格；隐藏时预设补满 12。
  const mergedPresetCount = canCreate ? PRESET_DISPLAY_COUNT_MERGED : PRESET_DISPLAY_COUNT;
  // UGC 块：新建卡在最前占 1 槽，剩余槽给自建角色。
  const ugcSlots = canCreate ? UGC_ROW_SLOTS - 1 : UGC_ROW_SLOTS;

  return (
    <>
      {/* 角色区：有 UGC 时拆「我的角色」+「官方角色」两块；无 UGC 时合并为新建卡 + 预设单块 */}
      <div className={cn('relative z-10 mx-auto mt-6 w-full max-w-[1440px] px-8 sm:px-4', className)}>
        {isLoading ? (
          <div className="py-6" id="banner-roles">
            <div className="grid grid-cols-6 items-stretch gap-4 sm:grid-cols-2 sm:gap-1">
              <BannerAvatarCardSkeleton className="w-full sm:w-full" />
              {BANNER_SKELETON_KEYS.map((key) => (
                <BannerAvatarCardSkeleton key={key} className="w-full sm:w-full" />
              ))}
            </div>
          </div>
        ) : hasUgc ? (
          <>
            {/* UGC 块：新建卡（未达上限）在最前，后接自建角色 */}
            <div className="font-normal text-white text-xl">{t('vidu-stream.banner.myCharacter')}</div>
            <div className="py-6 pb-12">
              <div className="grid grid-cols-6 items-stretch gap-4 sm:grid-cols-2 sm:gap-1">
                {canCreate ? renderCreateCard() : null}
                {ugcRoles.slice(0, ugcSlots).map(renderUgcCard)}
              </div>
            </div>
            {/* PGC 块：官方预设 12 个 */}
            <div className="font-normal text-white text-xl">{t('vidu-stream.banner.title')}</div>
            <div className="py-6" id="banner-roles">
              <div className="grid grid-cols-6 items-stretch gap-4 sm:grid-cols-2 sm:gap-1">
                {presetRoles.slice(0, PRESET_DISPLAY_COUNT).map(renderPresetCard)}
              </div>
            </div>
          </>
        ) : (
          <>
            {canCreate ? (
              <div className="text-center font-normal text-sm text-white/40">
                {t('vidu-stream.banner.selectOrCreate')}
              </div>
            ) : (
              <div className="font-normal text-white text-xl">{t('vidu-stream.banner.title')}</div>
            )}
            <div className="py-6" id="banner-roles">
              <div className="grid grid-cols-6 items-stretch gap-4 sm:grid-cols-2 sm:gap-1">
                {canCreate ? renderCreateCard() : null}
                {presetRoles.slice(0, mergedPresetCount).map(renderPresetCard)}
              </div>
            </div>
          </>
        )}
      </div>

      {isCreateMyOwnOpen ? (
        <CreateMyOwnScreen
          initialAvatarData={createMyOwnInitialData}
          onBack={handleCloseCreateMyOwn}
          onConfirmSuccess={() => {
            void refreshBannerRoles();
          }}
        />
      ) : null}

      {modals}

      {shareTarget && (
        <ShareAvatarDialog
          role={shareTarget}
          open={shareTarget !== null}
          onOpenChange={(open) => !open && setShareTarget(null)}
          shareUrl={
            shareTarget?.id?.trim() ? `${window.location.origin}/vidu-stream/share-avatar/${shareTarget.id.trim()}` : ''
          }
        />
      )}
    </>
  );
}
